/**
 * Parse Integration Suite iFlow metadata from panel JSON blocks (agent / MCP output).
 */

function asObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value;
}

function pickString(obj, keys) {
  for (const key of keys) {
    const v = obj?.[key];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

/** @typedef {{ iflowName: string, iflowId: string, packageId: string, packageName: string, version: string, status: string, designerUrl: string, endpointUrl: string }} IflowArtifactMeta */

/**
 * @param {unknown} parsed
 * @returns {IflowArtifactMeta | null}
 */
export function iflowMetaFromParsedJson(parsed) {
  const obj = asObject(parsed);
  if (!obj) return null;

  const kind = pickString(obj, ['artifactKind', 'kind', 'type']).toLowerCase();
  const iflowName = pickString(obj, ['iflowName', 'iflow_name', 'name', 'integrationFlowName']);
  const iflowId = pickString(obj, ['iflowId', 'iflow_id', 'id', 'integrationFlowId']);
  const packageId = pickString(obj, ['packageId', 'package_id', 'contentPackageId']);
  const packageName = pickString(obj, ['packageName', 'package_name', 'contentPackageName']);

  const looksLikeIflow =
    kind === 'iflow' ||
    kind === 'integration_flow' ||
    Boolean(iflowName) ||
    Boolean(iflowId && (packageId || packageName));

  if (!looksLikeIflow) return null;

  return {
    iflowName: iflowName || iflowId || 'Integration flow',
    iflowId,
    packageId,
    packageName,
    version: pickString(obj, ['version', 'iflowVersion']),
    status: pickString(obj, ['status', 'deploymentStatus', 'state']),
    designerUrl: pickString(obj, ['designerUrl', 'designer_url', 'integrationSuiteUrl', 'url']),
    endpointUrl: pickString(obj, ['endpointUrl', 'endpoint_url', 'httpUrl', 'address']),
  };
}

/**
 * @param {Array<{ type?: string, parsed?: unknown, content?: string }>} blocks
 * @returns {IflowArtifactMeta | null}
 */
export function extractIflowArtifactMeta(blocks) {
  const list = Array.isArray(blocks) ? blocks : [];
  for (const block of list) {
    if (String(block?.type || '').toLowerCase() !== 'json') continue;
    if (block.parsed) {
      const meta = iflowMetaFromParsedJson(block.parsed);
      if (meta) return meta;
    }
    try {
      const parsed = JSON.parse(String(block.content || ''));
      const meta = iflowMetaFromParsedJson(parsed);
      if (meta) return meta;
    } catch {
      /* ignore */
    }
  }
  return null;
}

const MERMAID_HEAD_RE = /^(flowchart|graph|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|mindmap|timeline|gitGraph|C4Context)\b/i;

export function looksLikeMermaidSource(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  return MERMAID_HEAD_RE.test(s);
}

/** Normalize agent MCP mermaid for browser rendering. */
export function normalizeMermaidSource(text) {
  let s = String(text || '').trim();
  if (!s) return s;
  s = s.replace(/<br\s*\/>/gi, '<br>');
  return s;
}

/**
 * When panel_blocks are missing but artifact_summary is a diagram, synthesize a mermaid block.
 * @param {Array<{ type?: string, content?: string }>} blocks
 * @param {string} summary
 */
export function coerceIntegrationSuitePanelBlocks(blocks, summary = '') {
  const list = Array.isArray(blocks) ? blocks.filter((b) => b && String(b.content || '').trim()) : [];
  if (list.length) return list;
  const summaryText = String(summary || '').trim();
  if (looksLikeMermaidSource(summaryText)) {
    return [{ type: 'mermaid', content: summaryText, title: 'Flow diagram' }];
  }
  if (summaryText.length >= 32) {
    return [{ type: 'markdown', content: summaryText, title: 'Integration Suite' }];
  }
  return list;
}

/** Prefer diagram tab when multiple panel blocks exist. */
export function sortIntegrationSuitePanelBlocks(blocks) {
  const list = Array.isArray(blocks) ? [...blocks] : [];
  const rank = (b) => {
    const t = String(b?.type || 'markdown').toLowerCase();
    if (t === 'mermaid') return 0;
    if (t === 'html') return 1;
    if (t === 'markdown') return 2;
    if (t === 'json') return 3;
    return 4;
  };
  return list.sort((a, b) => rank(a) - rank(b));
}
